import { memo } from 'react';
import styles from './PrecipitationCell.module.css';

interface PrecipitationCellProps {
  value: number | null;
  snow: number;
  freezing_rain_prob: number;
}

// Interpolate between two hex colors
const interpolateColor = (color1: string, color2: string, factor: number) => {
  const int1 = parseInt(color1.substring(1), 16);
  const int2 = parseInt(color2.substring(1), 16);
  
  const r1 = (int1 >> 16) & 0xff;
  const g1 = (int1 >> 8) & 0xff;
  const b1 = int1 & 0xff;
  
  const r2 = (int2 >> 16) & 0xff;
  const g2 = (int2 >> 8) & 0xff;
  const b2 = int2 & 0xff;
  
  const r = Math.round(r1 + (r2 - r1) * factor);
  const g = Math.round(g1 + (g2 - g1) * factor);
  const b = Math.round(b1 + (b2 - b1) * factor);
  
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
};

// Get base color based on snow value using three color stops
const getSnowColor = (snow: number) => {
  const GREEN = '#4caf50';
  const YELLOW = '#ffeb3b';
  const BLUE = '#2196f3';
  
  if (snow <= 0.5) {
    // Interpolate between green and yellow
    return interpolateColor(GREEN, YELLOW, snow * 2);
  } else {
    // Interpolate between yellow and blue
    return interpolateColor(YELLOW, BLUE, (snow - 0.5) * 2);
  }
};

// Get final color by interpolating between snow color and purple based on freezing rain probability
const getFinalColor = (snow: number, freezing_rain_prob: number) => {
  const baseColor = getSnowColor(snow);
  const PURPLE = '#9c27b0';
  
  // Scale from base color to purple based on freezing rain probability (0-1)
  return interpolateColor(baseColor, PURPLE, freezing_rain_prob);
};

const PrecipitationCell = memo(({ value, snow, freezing_rain_prob }: PrecipitationCellProps) => {
  if (value === null) {
    return <div className={styles.nullValue}>-</div>;
  }

  const maxRange = 0.1; // Maximum precipitation value for scaling
  const height = Math.min(Math.max((value / maxRange) * 50, 0), 50);
  
  return (
    <div className={styles.container}>
      <div 
        className={styles.bar}
        style={{
          height: `${height}%`,
          backgroundColor: getFinalColor(snow, freezing_rain_prob)
        }} 
      />
      <div className={styles.value}>
        {(value === 0 ? '0' : value.toFixed(2).replace(/^0/, ''))}
      </div>
    </div>
  );
});

export default PrecipitationCell;
